import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  ChevronUpDownIcon,
  DocumentTextIcon,
  CalculatorIcon,
  CalendarIcon,
  ChartBarIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  DocumentIcon,
  CogIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

// Helper function to generate page numbers with ellipsis
function getPageNumbers(currentPage, totalPages) {
  const delta = 2; // Number of pages to show around current page
  const range = [];
  const rangeWithDots = [];

  for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
    range.push(i);
  }

  if (currentPage - delta > 2) {
    rangeWithDots.push(1, '...');
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage + delta < totalPages - 1) {
    rangeWithDots.push('...', totalPages);
  } else {
    rangeWithDots.push(totalPages);
  }

  return rangeWithDots.filter((item, index, arr) => arr.indexOf(item) === index && item !== 1 || index === 0).slice(0, totalPages);
}

function KeywordTable({ keywords, search, showingAllKeywords, allKeywords, pagination }) {
  // Read initial values from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const [sortBy, setSortBy] = useState(urlParams.get('sort') || '');
  const [sortDirection, setSortDirection] = useState(urlParams.get('direction') || '');
  const [filterBy, setFilterBy] = useState(urlParams.get('buildable') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(urlParams.get('category') || 'all');
  const [typeFilter, setTypeFilter] = useState(urlParams.get('type') ? urlParams.get('type').split(',') : []);
  const [effortFilter, setEffortFilter] = useState(urlParams.get('effort') ? urlParams.get('effort').split(',') : []);
  const [monetizeFilter, setMonetizeFilter] = useState(urlParams.get('monetize') ? urlParams.get('monetize').split(',') : []);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [showEffortFilter, setShowEffortFilter] = useState(false);
  const [showBuildableFilter, setShowBuildableFilter] = useState(false);
  
  // Refs for click outside detection
  const categoryFilterRef = useRef(null);
  const typeFilterRef = useRef(null);
  const effortFilterRef = useRef(null);
  const buildableFilterRef = useRef(null);

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryFilterRef.current && !categoryFilterRef.current.contains(event.target)) {
        setShowCategoryFilter(false);
      }
      if (typeFilterRef.current && !typeFilterRef.current.contains(event.target)) {
        setShowTypeFilter(false);
      }
      if (effortFilterRef.current && !effortFilterRef.current.contains(event.target)) {
        setShowEffortFilter(false);
      }
      if (buildableFilterRef.current && !buildableFilterRef.current.contains(event.target)) {
        setShowBuildableFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTypeIcon = (type) => {
    const iconClass = "w-4 h-4 text-gray-600";
    const icons = {
      test: <DocumentTextIcon className={iconClass} />,
      calculator: <CalculatorIcon className={iconClass} />, 
      planner: <CalendarIcon className={iconClass} />,
      tracker: <ChartBarIcon className={iconClass} />,
      tool: <WrenchScrewdriverIcon className={iconClass} />,
      affiliate: <CurrencyDollarIcon className={iconClass} />,
      content: <DocumentIcon className={iconClass} />
    };
    return icons[type] || <CogIcon className={iconClass} />;
  };

  const getCategoryBadge = (category) => {
    if (category === 'simple') {
      return (
        <span className="px-3 py-1.5 bg-green-500/20 text-green-300 text-xs rounded-lg border border-green-500/30 font-medium">
          Quick Deploy
        </span>
      );
    } else if (category === 'advanced') {
      return (
        <span className="px-3 py-1.5 bg-blue-500/20 text-blue-300 text-xs rounded-lg border border-blue-500/30 font-medium">
          Complex Build
        </span>
      );
    }
    return null;
  };

  const getEffortBadge = (effort) => {
    const textColors = {
      1: 'text-green-400',
      2: 'text-green-400', 
      3: 'text-yellow-400',
      4: 'text-orange-400',
      5: 'text-red-400'
    };
    
    return (
      <span className={`text-xs font-medium ${textColors[effort] || textColors[3]}`}>
        {effort}/5
      </span>
    );
  };

  const toggleFavorite = async (keywordId, currentFavorited) => {
    try {
      const response = await fetch(`/keywords/${keywordId}/toggle_favorite`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Reload the page to reflect the updated favorites status
          window.location.reload();
        } else {
          console.error('Failed to toggle favorite:', result.error);
        }
      } else {
        console.error('Failed to toggle favorite:', response.statusText);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const deleteKeyword = async (keywordId, keywordName) => {
    if (!confirm(`Are you sure you want to delete the keyword "${keywordName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/keywords/${keywordId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Reload the page to reflect the deletion
          window.location.reload();
        } else {
          console.error('Failed to delete keyword:', result.error);
        }
      } else {
        console.error('Failed to delete keyword:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting keyword:', error);
    }
  };

  const deleteSearch = async (searchId, searchName, keywordsCount) => {
    if (!confirm(`Are you sure you want to delete "${searchName}"? This will delete all ${keywordsCount} keywords from this search.`)) {
      return;
    }

    try {
      const response = await fetch(`/searches/${searchId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Redirect to searches list after deletion
          window.location.href = '/';
        } else {
          console.error('Failed to delete search:', result.error);
        }
      } else {
        console.error('Failed to delete search:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting search:', error);
    }
  };

  const handleSort = (column) => {
    let newDirection = '';
    let newSortBy = '';
    
    if (sortBy === column) {
      // Cycle through: desc -> asc -> none
      if (sortDirection === 'desc') {
        newDirection = 'asc';
        newSortBy = column;
      } else if (sortDirection === 'asc') {
        newDirection = '';
        newSortBy = '';
      } else {
        newDirection = 'desc';
        newSortBy = column;
      }
    } else {
      // First click: sort descending
      newDirection = 'desc';
      newSortBy = column;
    }
    
    setSortBy(newSortBy);
    setSortDirection(newDirection);
    
    // Build URL with proper parameters
    const params = new URLSearchParams();
    if (newSortBy) params.set('sort', newSortBy);
    if (newDirection) params.set('direction', newDirection);
    if (filterBy !== 'all') params.set('buildable', filterBy);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  };

  const getSortIcon = (column) => {
    if (sortBy === column) {
      if (sortDirection === 'desc') {
        return <ChevronDownIcon className="w-4 h-4 text-blue-600" />;
      } else if (sortDirection === 'asc') {
        return <ChevronUpIcon className="w-4 h-4 text-blue-600" />;
      }
    }
    return <ChevronUpDownIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />;
  };

  const SortableHeader = ({ column, children, icon }) => (
    <th 
      className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 group transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center space-x-2">
        {icon && <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">{icon}</svg>}
        <span className="group-hover:text-white transition-colors">{children}</span>
        {getSortIcon(column)}
      </div>
    </th>
  );

  const updateFilters = (newFilters) => {
    const params = new URLSearchParams();
    
    // Add sorting params
    if (sortBy) params.set('sort', sortBy);
    if (sortDirection) params.set('direction', sortDirection);
    
    // Add filter params
    const filters = {
      buildable: filterBy,
      category: categoryFilter,
      type: typeFilter,
      effort: effortFilter,
      monetize: monetizeFilter,
      ...newFilters
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') {
        if (Array.isArray(value) && value.length > 0) {
          params.set(key, value.join(','));
        } else if (!Array.isArray(value)) {
          params.set(key, value);
        }
      }
    });
    
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  };

  const handleMultiFilterChange = (filterType, option, checked) => {
    let newValue;
    
    switch(filterType) {
      case 'type':
        newValue = checked 
          ? [...typeFilter, option]
          : typeFilter.filter(item => item !== option);
        setTypeFilter(newValue);
        break;
      case 'effort':
        newValue = checked 
          ? [...effortFilter, option]
          : effortFilter.filter(item => item !== option);
        setEffortFilter(newValue);
        break;
      case 'monetize':
        newValue = checked 
          ? [...monetizeFilter, option]
          : monetizeFilter.filter(item => item !== option);
        setMonetizeFilter(newValue);
        break;
      default:
        return;
    }
    
    updateFilters({ [filterType]: newValue });
  };

  const handleSingleFilterChange = (filterType, value) => {
    const newFilters = { [filterType]: value };
    
    // Update local state
    switch(filterType) {
      case 'buildable': setFilterBy(value); break;
      case 'category': setCategoryFilter(value); break;
    }
    
    updateFilters(newFilters);
  };

  const changePage = (page) => {
    const newUrl = new URL(window.location);
    if (page <= 1) {
      newUrl.searchParams.delete('page');
    } else {
      newUrl.searchParams.set('page', page);
    }
    window.location.href = newUrl.toString();
  };

  // Get unique values from allKeywords for filtering (unfiltered data)
  const filterData = allKeywords || keywords;
  const availableTypes = [...new Set(filterData?.map(k => k.ai_type).filter(Boolean))].sort();
  const availableCategories = [...new Set(filterData?.map(k => k.project_category).filter(Boolean))].sort();
  const availableMonetization = [...new Set(filterData?.map(k => k.ai_monetize).filter(Boolean))].sort();
  const availableEfforts = [...new Set(filterData?.map(k => k.ai_effort).filter(Boolean))].sort((a, b) => a - b);

  // Helper functions for formatting
  const formatVolume = (volume) => {
    if (!volume) return 'N/A';
    const num = parseInt(volume);
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1).replace('.0', '') + 'k';
    }
    return num.toLocaleString();
  };

  const getKdDisplay = (kd) => {
    if (kd === null || kd === undefined) return { 
      dotColor: 'bg-gray-400', 
      borderColor: 'border-gray-300', 
      text: 'N/A' 
    };
    
    const score = parseInt(kd);
    let dotColor, borderColor;
    
    if (score <= 40) {
      dotColor = 'bg-green-500/70'; // Easy - less transparent dot
      borderColor = 'border-green-500/50'; // Green border - more transparent
    } else if (score < 70) {
      dotColor = 'bg-yellow-500/70'; // Medium - less transparent dot
      borderColor = 'border-yellow-500/50'; // Yellow border - more transparent
    } else {
      dotColor = 'bg-red-500/70'; // Hard - less transparent dot
      borderColor = 'border-red-500/50'; // Red border - more transparent
    }
    
    return { dotColor, borderColor, text: score };
  };

  // Multi-select filter popup component
  const MultiFilterPopup = ({ title, options, currentValues, filterType, onClose, formatLabel }) => (
    <div className="absolute top-full left-0 mt-2 bg-slate-800/95 backdrop-blur-sm border border-slate-600/50 rounded-xl shadow-2xl p-4 z-50 min-w-56 max-h-72 overflow-y-auto">
      <div className="text-sm font-semibold text-white mb-3 flex items-center space-x-2">
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
        </svg>
        <span>{title}</span>
      </div>
      <div className="space-y-2">
        <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors">
          <input 
            type="checkbox"
            checked={currentValues.length === 0}
            onChange={(e) => {
              if (e.target.checked) {
                // Clear all selections
                switch(filterType) {
                  case 'type': setTypeFilter([]); break;
                  case 'effort': setEffortFilter([]); break;
                  case 'monetize': setMonetizeFilter([]); break;
                }
                updateFilters({ [filterType]: [] });
              }
            }}
            className="rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
          />
          <span className="font-medium text-white">All Targets</span>
        </label>
        {options.map(option => (
          <label key={option} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors">
            <input 
              type="checkbox"
              checked={currentValues.includes(option)}
              onChange={(e) => handleMultiFilterChange(filterType, option, e.target.checked)}
              className="rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
            />
            <span className="text-gray-300">{formatLabel ? formatLabel(option) : option}</span>
          </label>
        ))}
      </div>
      <button 
        onClick={onClose}
        className="mt-4 w-full text-xs text-gray-400 hover:text-gray-300 py-2 px-3 bg-slate-700/50 rounded-lg transition-colors"
      >
        Close Filter
      </button>
    </div>
  );

  // Single-select filter popup component
  const SingleFilterPopup = ({ title, options, currentValue, filterType, onClose, formatLabel }) => (
    <div className="absolute top-full left-0 mt-2 bg-slate-800/95 backdrop-blur-sm border border-slate-600/50 rounded-xl shadow-2xl p-4 z-50 min-w-56 max-h-72 overflow-y-auto">
      <div className="text-sm font-semibold text-white mb-3 flex items-center space-x-2">
        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
        </svg>
        <span>{title}</span>
      </div>
      <div className="space-y-2">
        <label className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors">
          <input 
            type="radio" 
            name={filterType}
            checked={currentValue === 'all'}
            onChange={() => handleSingleFilterChange(filterType, 'all')}
            className="border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
          />
          <span className="font-medium text-white">All Categories</span>
        </label>
        {options.map(option => (
          <label key={option} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors">
            <input 
              type="radio" 
              name={filterType}
              checked={currentValue === option}
              onChange={() => handleSingleFilterChange(filterType, option)}
              className="border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
            />
            <span className="text-gray-300">{formatLabel ? formatLabel(option) : option}</span>
          </label>
        ))}
      </div>
      <button 
        onClick={onClose}
        className="mt-4 w-full text-xs text-gray-400 hover:text-gray-300 py-2 px-3 bg-slate-700/50 rounded-lg transition-colors"
      >
        Close Filter
      </button>
    </div>
  );

  // Format functions for display
  const formatCategoryLabel = (category) => {
    return category === 'simple' ? 'Quick Build' : 'Advanced';
  };

  const formatTypeLabel = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-2xl">
      {/* Header with filters */}
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">
                {showingAllKeywords ? "Intelligence Matrix" : `Target Analysis: ${search?.name}`}
              </h3>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {!showingAllKeywords && search && (
              <button
                onClick={() => deleteSearch(search.id, search.name, keywords?.length || 0)}
                className="p-2 rounded-lg transition-all duration-200 text-gray-400 hover:text-red-400 hover:bg-red-500/10 border border-slate-600/50 hover:border-red-500/30"
                title="Delete search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            {search?.status && (
              <div className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                search.status === 'completed' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                search.status === 'running' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                search.status === 'failed' ? 'bg-red-500/20 text-red-300 border-red-500/30' :
                'bg-slate-700/50 text-slate-300 border-slate-600/50'
              }`}>
                <span className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    search.status === 'completed' ? 'bg-green-400' :
                    search.status === 'running' ? 'bg-yellow-400 animate-pulse' :
                    search.status === 'failed' ? 'bg-red-400' :
                    'bg-slate-400'
                  }`}></div>
                  <span>{search.status.charAt(0).toUpperCase() + search.status.slice(1)}</span>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-400 flex items-center space-x-4">
          <span className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
            <span>Click headers to sort</span>
          </span>
          <span className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
            <span>Use filters to refine data</span>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-900/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                <span className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  <span>Target Keyword</span>
                </span>
              </th>
              {showingAllKeywords && (
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  <span className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span>Source Search</span>
                  </span>
                </th>
              )}
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider relative" ref={typeFilterRef}>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <span className="hover:text-white transition-colors">Classification</span>
                  <button 
                    onClick={() => setShowTypeFilter(!showTypeFilter)}
                    className={`p-1.5 rounded-lg transition-all ${typeFilter.length > 0 ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'bg-slate-700/50 text-gray-400 hover:bg-slate-600/50 hover:text-gray-300'}`}
                    title="Filter by type"
                  >
                    <FunnelIcon className="w-3 h-3" />
                  </button>
                </div>
                {showTypeFilter && (
                  <MultiFilterPopup
                    title="Filter by Type"
                    options={availableTypes}
                    currentValues={typeFilter}
                    filterType="type"
                    onClose={() => setShowTypeFilter(false)}
                    formatLabel={formatTypeLabel}
                  />
                )}
              </th>
              <SortableHeader 
                column="volume" 
                icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />}
              >
                Volume Index
              </SortableHeader>
              <SortableHeader 
                column="kd" 
                icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />}
              >
                Difficulty Rating
              </SortableHeader>
              <th className="px-4 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 group relative transition-colors w-32" ref={effortFilterRef} onClick={() => handleSort('effort')}>
                <div className="flex items-center space-x-1">
                  <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="group-hover:text-white transition-colors text-xs">Level</span>
                  {getSortIcon('effort')}
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowEffortFilter(!showEffortFilter); }}
                    className={`p-1 rounded-md transition-all ${effortFilter.length > 0 ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'bg-slate-700/50 text-gray-400 hover:bg-slate-600/50 hover:text-gray-300'}`}
                    title="Filter by effort"
                  >
                    <FunnelIcon className="w-2.5 h-2.5" />
                  </button>
                </div>
                {showEffortFilter && (
                  <MultiFilterPopup
                    title="Filter by Effort"
                    options={availableEfforts}
                    currentValues={effortFilter}
                    filterType="effort"
                    onClose={() => setShowEffortFilter(false)}
                  />
                )}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider relative" ref={categoryFilterRef}>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="hover:text-white transition-colors">Build Category</span>
                  <button 
                    onClick={() => setShowCategoryFilter(!showCategoryFilter)}
                    className={`p-1.5 rounded-lg transition-all ${categoryFilter !== 'all' ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'bg-slate-700/50 text-gray-400 hover:bg-slate-600/50 hover:text-gray-300'}`}
                    title="Filter by category"
                  >
                    <FunnelIcon className="w-3 h-3" />
                  </button>
                </div>
                {showCategoryFilter && (
                  <SingleFilterPopup
                    title="Filter by Category"
                    options={availableCategories}
                    currentValue={categoryFilter}
                    filterType="category"
                    onClose={() => setShowCategoryFilter(false)}
                    formatLabel={formatCategoryLabel}
                  />
                )}
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-80">
                <span className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>Example</span>
                </span>
              </th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-300 uppercase tracking-wider w-16">
                {/* Actions column - no title */}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {keywords?.map((keyword, index) => (
              <tr 
                key={index} 
                className="hover:bg-slate-800/30 transition-colors group cursor-pointer"
                onClick={() => window.location.href = `/keywords/${keyword.id}`}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full opacity-60 group-hover:opacity-100 transition-opacity"></div>
                    <div>
                      <div className="text-sm font-medium text-white group-hover:text-cyan-300 transition-colors">{keyword.keyword}</div>
                      <div className="text-xs text-gray-400">origin: {keyword.seed}</div>
                    </div>
                  </div>
                </td>
                {showingAllKeywords && (
                  <td className="px-6 py-4">
                    <a 
                      href={`/searches/${keyword.search_id}`}
                      className="text-sm text-cyan-400 hover:text-cyan-300 hover:underline transition-colors flex items-center space-x-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <span>{keyword.search_name}</span>
                    </a>
                  </td>
                )}
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-cyan-400">{getTypeIcon(keyword.ai_type)}</span>
                    <span className="text-sm text-gray-300 capitalize">{keyword.ai_type}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                    <span className="text-sm font-medium text-white">{formatVolume(keyword.volume)}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className={`inline-flex items-center space-x-1.5 px-2 py-1 rounded-md border ${getKdDisplay(keyword.kd).borderColor} bg-slate-800/50`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${getKdDisplay(keyword.kd).dotColor}`}></div>
                    <span className="text-xs font-medium text-white">{getKdDisplay(keyword.kd).text}</span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  {keyword.ai_effort && getEffortBadge(keyword.ai_effort)}
                </td>
                <td className="px-6 py-4">
                  {getCategoryBadge(keyword.project_category)}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-300 font-medium">
                    {keyword.short_example}
                  </div>
                  {keyword.ai_notes && (
                    <div className="text-xs text-gray-500 mt-1 flex items-center space-x-1">
                      <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{keyword.ai_notes}</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(keyword.id, keyword.favorited);
                      }}
                      className={`p-1.5 rounded-md transition-all duration-200 ${
                        keyword.favorited 
                          ? 'text-pink-400 hover:text-pink-300 bg-pink-500/10 hover:bg-pink-500/20' 
                          : 'text-gray-400 hover:text-pink-400 hover:bg-pink-500/10'
                      }`}
                      title={keyword.favorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <svg className="w-4 h-4" fill={keyword.favorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteKeyword(keyword.id, keyword.keyword);
                      }}
                      className="p-1.5 rounded-md transition-all duration-200 text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                      title="Delete keyword"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700/50 bg-slate-900/30">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-400">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_keywords)} of {pagination.total_keywords} results
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Previous button */}
              <button
                onClick={() => changePage(pagination.current_page - 1)}
                disabled={pagination.current_page <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-300 bg-slate-700/50 rounded-lg border border-slate-600/50 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center space-x-1">
                {getPageNumbers(pagination.current_page, pagination.total_pages).map((page, index) => (
                  page === '...' ? (
                    <span key={index} className="px-3 py-2 text-sm text-gray-400">...</span>
                  ) : (
                    <button
                      key={index}
                      onClick={() => changePage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        page === pagination.current_page
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : 'text-gray-300 bg-slate-700/50 border border-slate-600/50 hover:bg-slate-600/50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>
              
              {/* Next button */}
              <button
                onClick={() => changePage(pagination.current_page + 1)}
                disabled={pagination.current_page >= pagination.total_pages}
                className="px-3 py-2 text-sm font-medium text-gray-300 bg-slate-700/50 rounded-lg border border-slate-600/50 hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {(!keywords || keywords.length === 0) && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 opacity-50">
              {search?.status === 'running' ? (
                <svg className="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {search?.status === 'running' ? 'Intelligence Scan in Progress' : 'No Targets Detected'}
            </h3>
            <p className="text-gray-400">
              {search?.status === 'running' ? 'Analyzing keyword data and generating insights...' : 'No keywords found matching current filters'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default KeywordTable;